import { LucideIcon } from 'lucide-react';

import { PATHS } from 'router/config';
import { UserRole } from 'types';

import type { IMenuGroup, IMenuLeaf } from './MenuContainer';

/**
 * ТЗ 0121 — модель мобильной навигации (панель вкладок и заголовок шапки).
 *
 * 🔴 Своей таблицы прав здесь нет и быть не должно. Вкладка-страница видна,
 * только если её адрес есть среди пунктов `getVisibleMenu` — той же функции,
 * что строит меню шапки: роли пункта, флаг подписи (`esign`) и прочее уже
 * применены там. Здесь — только КАКИЕ страницы вынести на панель для роли.
 */

export type TMobileTabKey =
  | 'leads'
  | 'contracts'
  | 'communicator'
  | 'tasks'
  | 'students'
  | 'financeSummary'
  | 'notifications';

/** Вкладка-страница → адрес пункта меню. `notifications` — не страница. */
const TAB_PATH: Record<Exclude<TMobileTabKey, 'notifications'>, string> = {
  leads: PATHS.leads,
  contracts: PATHS.contracts,
  communicator: PATHS.communicator,
  tasks: PATHS.dashboard,
  students: PATHS.students,
  financeSummary: PATHS.financesDashboard,
};

/**
 * Набор вкладок по роли (решение владельца 18.10). Если ролей несколько:
 * у админа — его набор (самая широкая роль); иначе наборы склеиваются в
 * порядке строк ниже без повторов и режутся до `MOBILE_TABS_MAX`.
 */
const TAB_SETS: { role: UserRole; tabs: TMobileTabKey[] }[] = [
  {
    role: UserRole.SALESMANAGER,
    tabs: ['leads', 'contracts', 'communicator', 'tasks'],
  },
  {
    role: UserRole.ACCOUNTANT,
    tabs: ['communicator', 'students', 'tasks', 'notifications'],
  },
  { role: UserRole.FINANCIER, tabs: ['financeSummary', 'contracts', 'tasks'] },
  { role: UserRole.ADMIN, tabs: ['financeSummary', 'contracts', 'tasks'] },
];

/** Сколько вкладок-страниц максимум (пятая — «Ещё»). */
export const MOBILE_TABS_MAX = 4;

export interface IMobileTab {
  key: string;
  /** Ключ подписи (`mobile.tabs.*`); у запасных вкладок — `null`. */
  labelKey: string | null;
  /** Подпись пункта меню — для запасных вкладок и как полное имя. */
  text: string;
  /** Адрес страницы; у «Уведомлений» — `null` (открывают панель). */
  to: string | null;
  /** Префикс адреса для подсветки — как у пункта меню. */
  match: string | null;
  icon: LucideIcon | null;
}

/**
 * Ключи вкладок для набора ролей: админ — свой набор; несколько других
 * ролей — наборы подряд без повторов. `null` — у ролей набора нет.
 */
export const tabKeysFor = (userRoles: UserRole[]): TMobileTabKey[] | null => {
  const admin = TAB_SETS.find(({ role }) => role === UserRole.ADMIN);
  if (admin && userRoles.includes(UserRole.ADMIN)) return admin.tabs;
  const sets = TAB_SETS.filter(({ role }) => userRoles.includes(role));
  if (!sets.length) return null;
  return Array.from(new Set(sets.flatMap(({ tabs }) => tabs)));
};

const leafByPath = (groups: IMenuGroup[]): Map<string, IMenuLeaf> => {
  const map = new Map<string, IMenuLeaf>();
  groups.forEach((group) =>
    group.children.forEach((leaf) => {
      if (!map.has(leaf.to)) map.set(leaf.to, leaf);
    }),
  );
  return map;
};

const leafTab = (leaf: IMenuLeaf, labelKey: string | null): IMobileTab => ({
  key: leaf.to,
  labelKey,
  text: leaf.text,
  to: leaf.to,
  match: leaf.match ?? leaf.to,
  icon: leaf.icon,
});

/**
 * Вкладки панели для пользователя — без «Ещё» (её панель добавляет всегда).
 *
 * Роль без своего набора (методист, HR, маркетинг) получает первые пункты
 * своего же меню — то, что ей и так видно.
 */
export const getMobileTabs = (
  userRoles: UserRole[],
  visibleMenu: IMenuGroup[],
): IMobileTab[] => {
  const leaves = leafByPath(visibleMenu);
  const keys = tabKeysFor(userRoles);

  if (!keys) {
    return Array.from(leaves.values())
      .slice(0, MOBILE_TABS_MAX)
      .map((leaf) => leafTab(leaf, null));
  }

  return keys
    .map((key): IMobileTab | null => {
      if (key === 'notifications') {
        return {
          key,
          labelKey: `mobile.tabs.${key}`,
          text: '',
          to: null,
          match: null,
          icon: null,
        };
      }
      const leaf = leaves.get(TAB_PATH[key]);
      return leaf ? leafTab(leaf, `mobile.tabs.${key}`) : null;
    })
    .filter((tab): tab is IMobileTab => Boolean(tab))
    .slice(0, MOBILE_TABS_MAX);
};

/** True when the current path equals the base or is nested under it. */
export const isPathActive = (pathname: string, base: string): boolean =>
  pathname === base || pathname.startsWith(`${base}/`);

/**
 * Пункт меню текущей страницы — для заголовка мобильной шапки. Самый
 * длинный совпавший адрес: `/finances/invoices` важнее `/finances`.
 */
export const findActiveLeaf = (
  pathname: string,
  visibleMenu: IMenuGroup[],
): IMenuLeaf | null =>
  visibleMenu
    .flatMap((group) => group.children)
    .reduce<IMenuLeaf | null>((best, leaf) => {
      const base = leaf.match ?? leaf.to;
      if (!isPathActive(pathname, base)) return best;
      if (!best || base.length > (best.match ?? best.to).length) return leaf;
      return best;
    }, null);
